import {defineComponent, onMounted, ref, watch} from "vue";
import {AllPurchaseInvoicesQuery, PurchaseInvoice} from "@/api/enterprise-gateway-api.proxy.ts";
import {type PurchaseInvoicesResponse, useInvoicingStore} from "@/app/stores/invoicing-store.ts";
import {storeToRefs} from "pinia";
import {PagedResult} from "@/app/shared/helpers/paged-result.ts";
import {SearchPurchaseInvoicesQueryMapper, useSearchQueryStore} from "@/app/stores/search-query-store.ts";
import {useRoute} from "vue-router";

export default defineComponent({
    name: 'AegPurchaseInvoicesTab',
    setup() {
        const invoiceStore = useInvoicingStore();
        const searchSessionQueryStore = useSearchQueryStore();
        const route = useRoute();

        const {isLoading} = storeToRefs(invoiceStore);
        const purchaseInvoices = ref<PurchaseInvoicesResponse>({pagedResult: PagedResult.createEmpty<PurchaseInvoice>(), downloadUrl: ''});
        const searchQuery = ref<AllPurchaseInvoicesQuery>(AllPurchaseInvoicesQuery.fromJS({
            pageSize: 50,
            page: 1
        }));
        const defaultSearchQuery = ref<AllPurchaseInvoicesQuery>();

        const toInvoicesQuery = (queryParams: Record<string, unknown>) => {
            const fromQueryParams = SearchPurchaseInvoicesQueryMapper.fromQueryParams(queryParams);

            return AllPurchaseInvoicesQuery.fromJS({
                fromDate: fromQueryParams.fromDate,
                toDate: fromQueryParams.toDate,
                docNr: fromQueryParams.docNr,
                service: fromQueryParams.service,
                pageSize: fromQueryParams.pageSize ?? 50,
                page: fromQueryParams.page ?? 1,
            });
        };

        const applyRouteQuery = (queryParams: Record<string, unknown>) => {
            defaultSearchQuery.value = SearchPurchaseInvoicesQueryMapper.fromQueryParams(queryParams);
            searchQuery.value = toInvoicesQuery(queryParams);
        };

        const loadPurchaseInvoices = async () => {
            const result = await invoiceStore.loadPurchaseInvoicesForAdmin(searchQuery.value);
            if (!result) {
                return;
            }
            purchaseInvoices.value = result;
        };

        const goToPage = async (page: number) => {
            searchQuery.value.page = page;
            await searchSessionQueryStore.syncUrlToPurchaseInvoicesQueryIfChanged(searchQuery.value);
            await loadPurchaseInvoices();
        };

        const onSearch = async (searchCriteria: AllPurchaseInvoicesQuery) => {
            searchQuery.value.page = 1;
            searchCriteria.page = searchQuery.value.page;
            searchCriteria.pageSize = searchQuery.value.pageSize;
            searchQuery.value = searchCriteria;
            await loadPurchaseInvoices();
            await searchSessionQueryStore.syncUrlToPurchaseInvoicesQueryIfChanged(searchQuery.value);
        };

        watch(
            () => route.query,
            async (queryParams) => {
                const next = SearchPurchaseInvoicesQueryMapper.fromQueryParams(queryParams as Record<string, unknown>);
                const same = SearchPurchaseInvoicesQueryMapper.compareSearchSessionQueryFromParams(searchQuery.value, next);
                if (!same) {
                    applyRouteQuery(queryParams as Record<string, unknown>);
                    await loadPurchaseInvoices();
                }
            }
        );

        onMounted(async () => {
            if (Object.keys(route.query).length > 0) {
                applyRouteQuery(route.query as Record<string, unknown>);
            }

            await loadPurchaseInvoices();
        });

        return {
            isLoading,
            purchaseInvoices,
            defaultSearchQuery: defaultSearchQuery,
            onSearch,
            goToPage,
        };
    }
});


